import React from 'react';

interface ChatWithCedarIconProps {
  size?: 'small' | 'medium' | 'large';
  className?: string;
  style?: React.CSSProperties;
  /** When used in bottom navigation, match nav active/hover behavior */
  variant?: 'inherit' | 'nav' | 'cta';
  active?: boolean;
  hover?: boolean;
  /** When tab is being clicked (mousedown), show teal */
  pressed?: boolean;
}

export const ChatWithCedarIcon: React.FC<ChatWithCedarIconProps> = ({ 
  size = 'medium', 
  className = '',
  style,
  variant = 'inherit',
  active = false,
  hover = false,
  pressed = false,
}) => {
  const sizeClasses = {
    small: 'w-4 h-4',
    medium: 'w-5 h-5', 
    large: 'w-6 h-6'
  };

  // Teal when hover or pressed; cedar dark when selected; gray when inactive
  const useTeal = hover || pressed;
  const navFillColor = useTeal ? '#42A5A5' : (active ? '#1f6c4f' : 'none');
  const navStrokeColor = useTeal ? '#42A5A5' : (active ? '#1f6c4f' : '#6b7a6e');
  const navTextColor = (useTeal || active) ? '#ffffff' : '#6b7a6e';

  const ctaFillColor = hover ? 'currentColor' : 'none'; // currentColor should be white in CTAs
  const ctaStrokeColor = 'currentColor';

  const fill =
    variant === 'nav' ? navFillColor :
    variant === 'cta' ? ctaFillColor :
    'none';
  const stroke =
    variant === 'nav' ? navStrokeColor :
    variant === 'cta' ? ctaStrokeColor :
    'currentColor';
  // The bubble shape is only ~14px tall in a 24px viewBox (y=2..16).
  // Bubble center is at y=9, but viewBox center is at y=12, so bubble is 3px too high.
  // For CTA buttons, we need to center the bubble vertically to align with text.
  const bubbleTransform =
    variant === 'nav'
      ? undefined
      : variant === 'cta'
        ? 'translate(0 3)' // CTA buttons - move bubble down 3px to center it in viewBox (y=9 -> y=12)
        : 'translate(0 4)';

  return (
    <svg 
      className={`${sizeClasses[size]} ${className}`}
      viewBox="0 0 24 24" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
      style={{ 
        display: 'inline-block', 
        verticalAlign: 'middle',
        flexShrink: 0,
        margin: 0,
        padding: 0,
        ...style 
      }}
    >
      {/* Cedar chat bubble: 3 rounded corners, bottom-left square (matches bottom nav) */}
      <path 
        /* 3 rounded corners (top-left/top-right/bottom-right), bottom-left square */
        d="M7 2H17A3 3 0 0 1 20 5V13A3 3 0 0 1 17 16H8H4V5A3 3 0 0 1 7 2Z"
        transform={bubbleTransform}
        fill={fill}
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Nav CC monogram - always visible (hidden logo branding) */}
      {variant === 'nav' && (
        <text
          x="12"
          y="11"
          textAnchor="middle"
          fontSize="7"
          fontWeight="700"
          fill={navTextColor}
          fontFamily="Inter, sans-serif"
        >
          CC
        </text>
      )}

      {/* CTA-only CC monogram on hover (white bubble, green CC) */}
      {variant === 'cta' && hover && (
        <text
          x="12"
          y="16"
          textAnchor="middle"
          fontSize="7"
          fontWeight="700"
          fill="#1f6c4f"
          fontFamily="Inter, sans-serif"
        >
          CC
        </text>
      )}
    </svg>
  );
}; 